/**
 * FAV-001 Favorite Button Component
 * Offline-first, Edge-first, desired-state model
 */

import React, { useState, useCallback, useEffect, forwardRef } from 'react';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { cn } from '@/lib/utils';
import { useFavoritesStore, type FavoriteEntityType } from '@/lib/services/useFavoritesStore';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import heartActiveIcon from '@/assets/heart_active.png';
import heartInactiveIcon from '@/assets/heart_inactive.png';

export type FavoriteButtonProps = {
  entityType: FavoriteEntityType;
  entityId: string;
  initialState?: boolean;
  source?: 'catalog' | 'pet' | 'favorites_list';
  size?: 24 | 28;
  backgroundMode?: 'onPhoto' | 'onLight';
  className?: string;
  onAuthRequired?: () => void;
};

// Tooltip shown only once
const TOOLTIP_STORAGE_KEY = 'drug_fav_tooltip_shown';

function useTooltipOnce(): [boolean, () => void] {
  const [shown, setShown] = useState(() => {
    return localStorage.getItem(TOOLTIP_STORAGE_KEY) === 'true';
  });

  const markShown = useCallback(() => {
    localStorage.setItem(TOOLTIP_STORAGE_KEY, 'true');
    setShown(true);
  }, []);

  return [shown, markShown];
}


export const FavoriteButton = forwardRef<HTMLDivElement, FavoriteButtonProps>(function FavoriteButton({
  entityType,
  entityId,
  initialState,
  source = 'catalog',
  size = 24,
  backgroundMode = 'onPhoto',
  className,
  onAuthRequired,
}, ref) {
  const navigate = useSafeNavigate();
  const { isAuthenticated } = useAuth();
  const isGuest = !isAuthenticated;
  const store = useFavoritesStore();
  const [tooltipShown, markTooltipShown] = useTooltipOnce();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  
  // Local optimistic state for instant visual feedback
  const [localState, setLocalState] = useState<boolean | null>(null);

  // Initialize from initialState if provided and not in local cache
  useEffect(() => {
    if (initialState !== undefined) {
      const currentState = store.isFavorited(entityType, entityId);
      // Only set if local cache doesn't have this entity
      if (currentState === false && initialState === true) {
        store.setLocal(entityType, entityId, initialState);
      }
    }
    // Reset local optimistic state when entityId changes
    setLocalState(null);
  }, [entityType, entityId, initialState, store]);

  // Get current state from store
  const storeState = store.isFavorited(entityType, entityId);
  const isPending = store.hasPending(entityType, entityId);
  
  // Use local optimistic state if available, otherwise use store state
  const isFavorited = localState !== null ? localState : storeState;

  // Sync local state with store state when store updates
  useEffect(() => {
    if (!isPending) {
      setLocalState(null);
    }
  }, [storeState, isPending]);

  // Show tooltip on first mount if not shown before
  useEffect(() => {
    if (!tooltipShown && !isFavorited) {
      setShowTooltip(true);
      const timer = setTimeout(() => {
        setShowTooltip(false);
        markTooltipShown();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [tooltipShown, isFavorited, markTooltipShown]);

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Auth check - redirect to login if not authenticated
    if (isGuest) {
      onAuthRequired?.();
      navigate('/auth/login');
      return;
    }

    // Hide tooltip on interaction
    if (showTooltip) {
      setShowTooltip(false);
      markTooltipShown();
    }

    // Optimistic update - immediately change visual state
    const newState = !isFavorited;
    setLocalState(newState);

    // Toggle in store (this handles sync)
    await store.toggleFavorite(entityType, entityId, source);

    // Show toast
    if (navigator.onLine) {
      toast({
        description: newState ? 'Добавлено в избранное' : 'Удалено из избранного',
        duration: 2000,
      });
    } else {
      toast({
        description: 'Сохранено. Синхронизируем позже',
        duration: 2000,
      });
    }
  }, [entityType, entityId, source, store, isGuest, onAuthRequired, showTooltip, markTooltipShown, isFavorited, navigate]);

  // Circle size based on icon size
  // Increased circle size to accommodate scaled icon (size * 1.1)
  const circleSize = size === 28 ? 44 : 40;

  return (
    <div ref={ref} className={cn('relative', className)}>
      {/* Tooltip */}
      {showTooltip && (
        <div 
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg whitespace-nowrap z-50"
          style={{ 
            backgroundColor: '#3F2516',
            fontFamily: 'CoFo Gothic, sans-serif',
            fontSize: '12px',
            color: 'white',
          }}
        >
          Сохраните, чтобы не потерять
          <div 
            className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent"
            style={{ borderTopColor: '#3F2516' }}
          />
        </div>
      )}

      {/* Button with circle background */}
      <button
        type="button"
        onClick={handleClick}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onMouseLeave={() => setIsPressed(false)}
        onTouchStart={() => setIsPressed(true)}
        onTouchEnd={() => setIsPressed(false)}
        className={cn(
          'flex items-center justify-center transition-transform duration-150',
          'min-w-[44px] min-h-[44px]',
          isPressed && 'scale-90'
        )}
        aria-label={isFavorited ? 'Удалить из избранного' : 'Добавить в избранное'}
        aria-pressed={isFavorited}
      >
        {/* Circle background */}
        <div 
          className={cn(
            'relative flex items-center justify-center rounded-full transition-all duration-200',
            isPending && 'opacity-70'
          )}
          style={{
            width: circleSize,
            height: circleSize,
            backgroundColor: '#FFFFFF',
            boxShadow: backgroundMode === 'onPhoto' 
              ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
              : '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <img 
            src={isFavorited ? heartActiveIcon : heartInactiveIcon}
            alt=""
            className="transition-transform duration-200 object-contain"
            style={{
              width: size,
              height: size,
              transform: isFavorited ? 'scale(1.1)' : 'scale(1)',
            }}
          />
        </div>
      </button>
    </div>
  );
});

export default FavoriteButton;
